import { Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel } from '@mui/material'
import { GpsFlagIcon } from 'assets/icons'
import LoadingSpinnerGreen from 'components/ui/LoadingSpinnerGreen'
import { useState } from 'react'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import { ClientRoute } from 'routes/client'
import styled from 'styled-components'

const getLocation = (course: Course) => {
  const { city, state, country } = course
  return `${city}, ${state ? `${state},` : ''} ${country}`
}

interface Props {
  courses: Course[]
  error: string | null
  status: RequestStatus
}

export default function CourseTable({ courses, error, status }: Props): JSX.Element {
  const intl = useIntl()
  const [order, setOrder] = useState<'asc' | 'desc'>('asc')
  const [orderBy, setOrderBy] = useState<string>('name')

  const tableHeaders = [
    { id: 'name', label: intl.formatMessage({ id: 'strings.courseName' }) },
    { id: 'clubName', label: intl.formatMessage({ id: 'strings.clubName' }) },
    { id: 'location', label: intl.formatMessage({ id: 'strings.location' }) },
    { id: 'gpsCoordinates', label: intl.formatMessage({ id: 'strings.gpsCoordinates' }) },
  ]

  const handleSort = (property: keyof Course) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const createSortedCourses = () => {
    return [...courses].sort((a, b) => {
      let compareA: string | number
      let compareB: string | number

      if (orderBy === 'location') {
        compareA = getLocation(a)
        compareB = getLocation(b)
      } else if (typeof a[orderBy] === 'string' && typeof b[orderBy] === 'string') {
        compareA = a[orderBy]
        compareB = b[orderBy]
      } else if (typeof a[orderBy] === 'number' && typeof b[orderBy] === 'number') {
        compareA = a[orderBy]
        compareB = b[orderBy]
      } else {
        return 0
      }

      return order === 'asc'
        ? compareA.toString().localeCompare(compareB.toString())
        : compareB.toString().localeCompare(compareA.toString())
    })
  }

  const sortedCourses = createSortedCourses()

  if (status === 'loading') {
    return (
      <LoadingContainer>
        <LoadingSpinnerGreen size={62} />
      </LoadingContainer>
    )
  }

  if ((status === 'succeeded' && courses.length === 0) || error) {
    return (
      <CoursesNotFound>
        <CoursesNotFoundTitle>{intl.formatMessage({ id: 'courseEditor.courseSearchErrorTitle' })}</CoursesNotFoundTitle>
        <p>{intl.formatMessage({ id: 'courseEditor.courseSearchErrorDescription' }, { br: <br /> })}</p>
      </CoursesNotFound>
    )
  }

  if (courses.length === 0) {
    return <div />
  }

  const getGpsFlag = (gpsPoints: number | null | undefined): JSX.Element => {
    const fullGpsPoints = 36

    if (!gpsPoints) {
      return <span />
    }

    return (
      <span>
        {gpsPoints < fullGpsPoints ? <GpsFlagIcon fill="#FCC224" /> : <GpsFlagIcon />}{' '}
        {intl.formatMessage({ id: 'strings.yes' })}
      </span>
    )
  }

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            {tableHeaders.map((header) => (
              <TableCell key={header.id}>
                <TableSortLabel
                  active={orderBy === header.id}
                  direction={orderBy === header.id ? order : 'asc'}
                  onClick={() => handleSort(header.id as keyof Course)}
                >
                  {header.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedCourses.map((course) => (
            <TableRow key={course.id}>
              <TableCell>
                <StyledLink to={ClientRoute.COURSE_EDITOR_COURSE(course.id)}>{course.name}</StyledLink>
              </TableCell>
              <TableCell>{course.clubName}</TableCell>
              <TableCell>{getLocation(course)}</TableCell>
              <TableCell>{getGpsFlag(course?.totalGpsPoints)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

const TableContainer = styled.div`
  margin: 60px 0 16px 0;
  width: 100%;
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
`

const CoursesNotFound = styled.div`
  align-items: center;
  background-color: #f1fbf1;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 282px;
  justify-content: center;
  margin-top: 100px;
  width: 85%;

  p {
    color: #424a4d;
    font-size: 16px;
    line-height: 150%;
    max-width: 400px;
    text-align: center;
  }
`

const CoursesNotFoundTitle = styled.h1`
  color: #032615;
  font-size: 17px;
  font-style: normal;
  font-weight: 500;
  text-align: center;
`

const StyledLink = styled(Link)`
  color: ${(props) => props.theme.palette.primary.main};
  &:hover {
    opacity: 0.85;
  }
`
